import { useEffect, useRef, useState } from 'react'
import {
  Button, Checkbox, CloseButton, Dialog, Field, HStack, Input, Portal,
  RadioGroup, Stack, Tag, Text,
} from '@chakra-ui/react'
import { LuLink, LuLock } from 'react-icons/lu'
import { useTranslation } from '../../i18n'
import { toaster } from '../ui/toaster'
import { previewVideoExport, requestVideoExport } from '../../services/videoExport'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * The share sheet for a bulk export, shaped like Drive's: who gets it, and
 * whether the link works for anyone who has it.
 *
 * The requester is always a recipient and cannot be removed — an export nobody
 * is told about is a bill with no reader.
 */
export function ExportDialog({ open, onOpenChange, eventId, videoCount }) {
  const { t } = useTranslation()
  const [emails, setEmails] = useState([])
  const [draft, setDraft] = useState('')
  const [draftInvalid, setDraftInvalid] = useState(false)
  const [access, setAccess] = useState('restricted')
  const [includeReels, setIncludeReels] = useState(false)
  const [busy, setBusy] = useState(false)
  const [preview, setPreview] = useState(null)
  // Separate from `preview === null`, which also means "no match found".
  const [previewFailed, setPreviewFailed] = useState(false)
  // Whether the admin has deliberately touched the access radio during this
  // open — once true, a late-arriving preview must never overwrite it.
  const accessTouched = useRef(false)
  // Same idea for the reels checkbox: the event type supplies the default
  // (on for weddings), and a deliberate tick must survive a late preview.
  const reelsTouched = useRef(false)

  useEffect(() => {
    accessTouched.current = false
    reelsTouched.current = false
    // ExportDialog is mounted once per videos page with no `key`, and the
    // router does not remount it when only `:eventId` changes — so without
    // clearing these, an admin who typed recipients for one event, cancelled,
    // and navigated to another event's videos page would find the previous
    // event's chips and access choice still sitting in the dialog.
    setEmails([])
    setDraft('')
    setDraftInvalid(false)
  }, [open, eventId])

  // Kept apart from the reset above because this one re-runs when the reels
  // checkbox moves: whether reels are in changes which videos the export
  // would hold, and so whether a built one already matches. Asking once on
  // open left a wedding with the box unticked reading "an export of these
  // exact videos was built on …" about an export that includes the reel.
  // Resetting the recipients on every tick would be its own bug.
  useEffect(() => {
    if (!open) return undefined
    let ignore = false
    previewVideoExport(eventId, { includeReels })
      .then((p) => {
        if (ignore) return
        setPreview(p)
        setPreviewFailed(false)
        if (!reelsTouched.current) {
          setIncludeReels(p?.includeReelsDefault === true)
        }
        if (p?.existing?.access && !accessTouched.current) {
          setAccess(p.existing.access)
        }
      })
      .catch((err) => {
        if (ignore) return
        // Degrading quietly here is what made a missing Firestore index look
        // like a missing feature: previewVideoExport threw for every event,
        // and the only symptom anyone could see was that the reels checkbox
        // never appeared.
        console.error('previewVideoExport failed', err)
        setPreview(null)
        setPreviewFailed(true)
      })
    return () => { ignore = true }
  }, [open, eventId, includeReels])

  const addDraft = () => {
    const value = draft.trim().toLowerCase()
    if (!value) return
    if (!EMAIL_RE.test(value)) {
      setDraftInvalid(true)
      return
    }
    if (!emails.includes(value)) setEmails((prev) => [...prev, value])
    setDraft('')
    setDraftInvalid(false)
  }

  const onDraftChange = (e) => {
    setDraft(e.target.value)
    setDraftInvalid(false)
  }

  const onKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      addDraft()
    }
  }

  const start = async (options = {}) => {
    setBusy(true)
    try {
      const result = await requestVideoExport({
        eventId,
        recipients: emails,
        access,
        includeReels,
        force: options.force === true,
      })
      toaster.create({
        title: result.reused
          ? t('exportDialog.reused')
          : t('exportDialog.started', { count: result.total }),
        description: t('exportDialog.emailWhenReady'),
        type: 'success',
      })
      onOpenChange({ open: false })
      setEmails([])
    } catch (error) {
      console.error('Export request failed:', error)
      toaster.create({ title: t('exportDialog.failed'), type: 'error' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <Dialog.Title>{t('exportDialog.title')}</Dialog.Title>
              <Dialog.CloseTrigger asChild><CloseButton size="sm" /></Dialog.CloseTrigger>
            </Dialog.Header>
            <Dialog.Body>
              <Stack gap={5}>
                <Text color="fg.muted">
                  {t('exportDialog.summary', { count: videoCount })}
                </Text>

                {previewFailed && (
                  <Text fontSize="sm" color="fg.warning">
                    {t('exportDialog.previewFailed')}
                  </Text>
                )}

                {preview?.existing && (
                  <Text fontSize="sm" color="fg.muted">
                    {preview.existing.status === 'ready'
                      ? t('exportDialog.reuseNotice', {
                        date: new Date(preview.existing.createdAt).toLocaleDateString(),
                      })
                      : t('exportDialog.runningNotice')}
                  </Text>
                )}

                {/* Only worth offering when the event actually has reels.
                    The default comes from the event type — on for weddings,
                    where the reel is usually the thing the couple wants. */}
                {preview?.reelCount > 0 && (
                  <Checkbox.Root
                    checked={includeReels}
                    onCheckedChange={(e) => {
                      reelsTouched.current = true
                      setIncludeReels(e.checked === true)
                    }}
                  >
                    <Checkbox.HiddenInput />
                    <Checkbox.Control />
                    <Checkbox.Label>
                      {t('exportDialog.includeReels', {
                        count: preview.reelCount,
                      })}
                    </Checkbox.Label>
                  </Checkbox.Root>
                )}

                <Field.Root invalid={draftInvalid}>
                  <Field.Label>{t('exportDialog.recipients')}</Field.Label>
                  <Input
                    value={draft}
                    onChange={onDraftChange}
                    onKeyDown={onKeyDown}
                    onBlur={addDraft}
                    placeholder={t('exportDialog.recipientsPlaceholder')}
                  />
                  <Field.HelperText>{t('exportDialog.recipientsHelp')}</Field.HelperText>
                  <Field.ErrorText>{t('exportDialog.invalidEmail')}</Field.ErrorText>
                  {emails.length > 0 && (
                    <HStack wrap="wrap" gap={2} pt={2}>
                      {emails.map((email) => (
                        <Tag.Root key={email} size="md">
                          <Tag.Label>{email}</Tag.Label>
                          <Tag.EndElement>
                            <Tag.CloseTrigger
                              aria-label={t('exportDialog.removeRecipient', { email })}
                              onClick={() => setEmails((p) => p.filter((e) => e !== email))}
                            />
                          </Tag.EndElement>
                        </Tag.Root>
                      ))}
                    </HStack>
                  )}
                </Field.Root>

                <Field.Root>
                  <Field.Label>{t('exportDialog.access')}</Field.Label>
                  <RadioGroup.Root
                    value={access}
                    onValueChange={(e) => {
                      accessTouched.current = true
                      setAccess(e.value)
                    }}
                  >
                    <Stack gap={3} pt={1}>
                      <RadioGroup.Item value="restricted">
                        <RadioGroup.ItemHiddenInput />
                        <RadioGroup.ItemIndicator />
                        <RadioGroup.ItemText>
                          <HStack gap={2}><LuLock /> {t('exportDialog.restricted')}</HStack>
                        </RadioGroup.ItemText>
                      </RadioGroup.Item>
                      <RadioGroup.Item value="public">
                        <RadioGroup.ItemHiddenInput />
                        <RadioGroup.ItemIndicator />
                        <RadioGroup.ItemText>
                          <HStack gap={2}><LuLink /> {t('exportDialog.public')}</HStack>
                        </RadioGroup.ItemText>
                      </RadioGroup.Item>
                    </Stack>
                  </RadioGroup.Root>
                  <Field.HelperText>
                    {access === 'public'
                      ? t('exportDialog.publicHelp')
                      : t('exportDialog.restrictedHelp')}
                  </Field.HelperText>
                </Field.Root>
              </Stack>
            </Dialog.Body>
            <Dialog.Footer>
              <Dialog.ActionTrigger asChild>
                <Button variant="outline">{t('common.cancel')}</Button>
              </Dialog.ActionTrigger>
              {preview?.existing?.status === 'ready' && (
                <Button
                  variant="outline"
                  loading={busy}
                  onClick={() => start({ force: true })}
                >
                  {t('exportDialog.rebuild')}
                </Button>
              )}
              <Button colorPalette="brand" onClick={start} loading={busy} disabled={videoCount === 0}>
                {t('exportDialog.start')}
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  )
}
